import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtIDR } from "@/lib/constants";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, Gem, Wallet, Award, Percent, DollarSign } from "lucide-react";

const PIE_COLORS = ["#10B981", "#D4AF37", "#EF4444"];

const KPI = ({ icon: Icon, label, value }) => (
  <div className="card-lux hover-lift p-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-burgundy/10 text-burgundy flex items-center justify-center"><Icon className="w-5 h-5" strokeWidth={1.6} /></div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">{label}</p>
        <p className="font-heading text-xl text-[#1A1A1A] leading-none mt-0.5">{value}</p>
      </div>
    </div>
  </div>
);

export default function Business() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/business").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-[#7A7A7A]">Memuat business dashboard...</div>;

  return (
    <div>
      <PageHeader overline="Owner Dashboard" title="Business" subtitle="Performa bisnis Anda dalam satu pandangan." />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6 stagger">
        <KPI icon={DollarSign} label="Revenue" value={fmtIDR(d.revenue)} />
        <KPI icon={TrendingUp} label="Est. Profit" value={fmtIDR(d.profit)} />
        <KPI icon={Gem} label="Total Weddings" value={d.num_weddings} />
        <KPI icon={Wallet} label="Avg Wedding Value" value={fmtIDR(d.avg_wedding_value)} />
        <KPI icon={Percent} label="Booking Conversion" value={`${d.booking_conversion}%`} />
        <KPI icon={Award} label="Best Package" value={d.best_package} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 card-lux p-5">
          <h3 className="font-heading text-xl text-burgundy mb-4">Revenue per Bulan</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={d.revenue_per_month}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#800020" stopOpacity={0.3} /><stop offset="95%" stopColor="#800020" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000000}jt`} />
              <Tooltip formatter={(v) => fmtIDR(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#800020" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-lux p-5">
          <h3 className="font-heading text-xl text-burgundy mb-4">Payment Status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={d.payment_status} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} innerRadius={45}>
                {d.payment_status.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtIDR(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-lux p-5">
          <h3 className="font-heading text-xl text-burgundy mb-4">Wedding per Bulan</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.wedding_per_month}>
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="weddings" fill="#D4AF37" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-lux p-5">
          <h3 className="font-heading text-xl text-burgundy mb-4">Package Performance</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.package_performance}>
              <XAxis dataKey="package" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000000}jt`} />
              <Tooltip formatter={(v) => fmtIDR(v)} />
              <Bar dataKey="revenue" fill="#800020" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
